import { Fragment, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    Icon,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    styled,
    Tooltip,
    Typography
} from '@mui/material';
import moment from 'moment';

import Form from 'Lib/components/form/Form';
import withForm from 'Lib/components/hoc/withForm';
import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import { allowBillShare, allowLeasePaymentShare, allowLeaseShare, allowWorkOrderShare } from 'Lib/constants/app/sharedAlertTypes';
import SidebarContext from 'Lib/contexts/SidebarContext';
import useApi from 'Lib/hooks/useApi';
import useNotify from 'Lib/hooks/useNotify';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import FromNow from 'Lib/utilities/fromNow';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

import DeleteDialog from '../dialog/DeleteDialog';
import SwitchField from '../form/SwitchField';
import TextField from '../form/TextField';

function AlertsButton({ toggleDrawer, ...props }) {
    const [isPortalOpen, setIsPortalOpen] = useState(false);
    const [portalAlerts, setPortalAlerts] = useState([]);

    const { mobile } = screenSize();

    const openPortal = alerts => {
        setPortalAlerts(alerts);
        setIsPortalOpen(true);
    };

    const closePortal = () => {
        setPortalAlerts([]);
        setIsPortalOpen(false);
    };

    return (
        <>
            <Tooltip title="Alerts" placement="top" arrow>
                <Box sx={{ mx: { xs: 0, sm: 2 } }}>
                    {mobile ? (
                        <IconButton onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.ALERTS)}>add_alert</IconButton>
                    ) : (
                        <IconButton variant="regular" onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.ALERTS)}>
                            add_alert
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
            <Alerts togglePortal={openPortal} {...props} />
            {isPortalOpen && createPortal(<AlertsModal alerts={portalAlerts} closePortal={closePortal} />, document.body)}
        </>
    );
}

const EmptyMessage = ({ toggleAdd }) => {
    return (
        <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Box
                sx={{
                    mb: 4,
                    height: 96,
                    width: 96,
                    bgcolor: 'success.secondary',
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Icon sx={{ color: 'success.main', fontSize: 56 }}>add_alert</Icon>
            </Box>
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(16) }}>
                It seems you haven&apos;t <br />
                created any alerts yet.
            </Typography>
            <Button variant="primary" sx={{ mt: 4, px: 20 }} onClick={toggleAdd}>
                New Alert
            </Button>
        </Container>
    );
};

export const AlertsModal = ({ alerts, closePortal }) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (alerts.length > 0) {
            setIsOpen(true);
        }
    }, []);

    const {
        components: { User }
    } = useContext(SidebarContext);

    const toggleShowAlerts = () => {
        setIsOpen(prevState => !prevState);
    };

    return (
        <Dialog
            open={isOpen}
            title={alerts.length > 1 ? 'Alerts' : 'Alert'}
            toggle={closePortal}
            onClose={closePortal}
            maxWidth="sm"
            actions={() => (
                <Button variant="primary" color="success" onClick={toggleShowAlerts}>
                    Ok
                </Button>
            )}
        >
            {alerts.map(({ alert }, index) => {
                const localTime = moment.utc(alert.dateTimeCreated).tz(moment.tz.guess());
                const formattedTime = localTime.format('MM-DD-YYYY [at] h:mm A z');

                return (
                    <Fragment key={alert.alertID}>
                        {index === 0 && <Divider sx={{ bgcolor: 'common.gray', mt: 3 }} />}
                        <Box display="flex" alignItems="center">
                            <Icon sx={{ mr: 2 }} fontSize="large">
                                notification_important
                            </Icon>
                            <Box py={4}>
                                <Typography
                                    sx={theme => ({
                                        maxWidth: '443px',
                                        fontSize: theme.typography.pxToRem(16),
                                        whiteSpace: 'break-spaces'
                                    })}
                                >
                                    {alert.message}
                                </Typography>
                                <Typography sx={theme => ({ fontSize: theme.typography.pxToRem(12), pt: 2 })}>
                                    Created by <User id={alert.createdByUserID} /> on {formattedTime}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ bgcolor: 'common.gray' }} />
                    </Fragment>
                );
            })}
        </Dialog>
    );
};

function Alerts(props) {
    const [showAdd, setShowAdd] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const [alerts, setAlerts] = useState([]);
    const [selectedAlert, setSelectedAlert] = useState({});

    const { toastSuccess, toastError } = useNotify();
    const isDrawerOpen = useSelector(state => state.ui.isDrawerOpen);

    const {
        sidebar,
        apis,
        components: { User }
    } = useContext(SidebarContext);

    const { contactTypeID, objectID, objectTypeID, alertShareObjectTypeID, sharedAlertObjectID, sharedObjectID } = sidebar;

    const postAlert = useApi(apis.updateAlert);
    const fetchAlerts = useApi(apis.getAlerts);
    const fetchSharedAlert = useApi(apis.getSharedAlert);
    const removeAlert = useApi(apis.deleteAlert);

    useEffect(() => {
        getAllAlerts();
    }, []);

    useEffect(() => {
        if (!showDelete) {
            setSelectedAlert({});
        }
    }, [showDelete]);

    useEffect(() => {
        if (!isDrawerOpen) {
            setShowAdd(false);
            setShowEdit(false);
            setSelectedAlert({});
        }
    }, [isDrawerOpen]);

    const getAllAlerts = async () => {
        try {
            const createdAlertsResponse = await fetchAlerts(objectTypeID, objectID);
            const createdAlerts = createdAlertsResponse.data;

            const sharedAlertsResponse = alertShareObjectTypeID && (await fetchSharedAlert(sharedObjectID, sharedAlertObjectID, alertShareObjectTypeID));

            const sharedAlerts = sharedAlertsResponse ? sharedAlertsResponse.data.map(alert => ({ alert })) : [];

            const allAlerts = [...createdAlerts, ...sharedAlerts];
            setAlerts(allAlerts);

            if (allAlerts.length > 0) {
                props.togglePortal(allAlerts);
            }
        } catch (error) {
            toastError(error);
        }
    };

    const toggleAdd = () => {
        setShowAdd(prevState => !prevState);
    };

    const toggleDelete = () => {
        setShowDelete(prevState => !prevState);
    };

    const handleDelete = () => {
        setIsLoading(true);

        removeAlert(selectedAlert.alertID)
            .then(() => {
                setAlerts(prevState => {
                    return prevState.filter(({ alert }) => alert.alertID !== selectedAlert.alertID);
                });

                setIsLoading(false);
                setShowDelete(false);
                setSelectedAlert({});
                toastSuccess('Successfully deleted alert');
            })
            .catch(error => {
                setIsLoading(false);
                toastError(error);
            });
    };

    const handleSubmit = (values, form) => {
        const payload = {
            ...values,
            objectID,
            objectTypeID
        };

        setIsLoading(true);

        props.form
            .handlePromise(form, [postAlert(selectedAlert.alertID, payload)], {
                successMessage: () => toastSuccess(showEdit ? 'Alert updated successfully' : 'Alert created successfully'),
                errorMessage: toastError
            })
            .then(response => {
                if (!selectedAlert.alertID) {
                    setAlerts(prevState => [response.data, ...prevState]);
                    setShowAdd(false);
                    setIsLoading(false);
                    form.resetForm();
                } else {
                    setAlerts(prevState => {
                        const updatedArray = [...prevState];

                        const index = updatedArray.findIndex(({ alert }) => alert.alertID === response.data.alert.alertID);

                        if (index !== -1) {
                            updatedArray[index] = response.data;
                        }

                        return updatedArray;
                    });

                    setShowEdit(false);
                    setSelectedAlert({});
                    setIsLoading(false);
                }
            })
            .catch(() => {
                setIsLoading(false);
            });
    };

    const renderDeleteAlert = () => {
        return (
            <DeleteDialog title="Delete Alert" buttonText="Delete Alert" open={showDelete} isDeleting={isLoading} toggle={toggleDelete} onDelete={handleDelete}>
                <Typography>Are you sure you want to permanently delete this alert?</Typography>
            </DeleteDialog>
        );
    };

    const MenuDropdown = ({ alert }) => {
        const [iconAnchorElement, setIconAnchorElement] = useState(null);

        return (
            <Stack>
                <IconButton onClick={event => setIconAnchorElement(event.currentTarget)}>more_vert</IconButton>
                <Menu
                    anchorEl={iconAnchorElement}
                    open={Boolean(iconAnchorElement)}
                    onClose={() => setIconAnchorElement(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            setSelectedAlert(alert);
                            setShowEdit(true);
                        }}
                    >
                        Edit
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setSelectedAlert(alert);
                            toggleDelete();
                        }}
                    >
                        Delete
                    </MenuItem>
                </Menu>
            </Stack>
        );
    };

    const AlertsList = () => {
        return (
            <Container sx={{ display: 'flex', flexDirection: 'column' }} disableGutters>
                <Button variant="primary" sx={{ mt: 4, px: 20, minWidth: '233px', alignSelf: 'center' }} onClick={toggleAdd}>
                    New Alert
                </Button>
                {/* We need to have the divider go across the entire container, so negative margins are needed */}
                <Divider sx={{ bgcolor: 'common.gray', my: 4, mx: -7 }} flexItem />
                {alerts.map(({ alert }, index) => {
                    const localTime = moment.utc(alert.dateTimeCreated).tz(moment.tz.guess());
                    const formattedTime = localTime.format('h:mm A z');

                    return (
                        <StyledCard key={index}>
                            <CardHeader
                                title={
                                    <Typography sx={theme => ({ fontWeight: 'bold', fontSize: theme.typography.pxToRem(16) })}>
                                        <User id={alert.createdByUserID} />
                                    </Typography>
                                }
                                action={<MenuDropdown alert={alert} />}
                            />
                            <CardContent sx={{ pt: 0 }}>
                                <Box display="flex" flexDirection="column">
                                    <Typography sx={{ mb: 4, whiteSpace: 'break-spaces' }} variant="italic">
                                        {alert.message}
                                    </Typography>
                                    {alert.isSharedOnLease === '1' && <Chip sx={{ alignSelf: 'start', my: 1 }} label="Shown on Lease" />}
                                    {alert.isSharedOnWorkOrder === '1' && <Chip sx={{ alignSelf: 'start', my: 1 }} label="Shown on Work Order" />}
                                    {alert.isSharedOnLeasePayment === '1' && <Chip sx={{ alignSelf: 'start', my: 1 }} label="Shown on Tenant Payment" />}
                                    {alert.isSharedOnBill === '1' && <Chip sx={{ alignSelf: 'start', my: 1 }} label="Shown on Bill" />}
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Box display="flex">
                                    <Icon sx={{ mr: 1 }}>calendar_month</Icon>
                                    <Typography
                                        sx={theme => {
                                            theme.typography.pxToRem(14);
                                        }}
                                    >
                                        <FromNow value={alert.dateTimeCreated} />
                                    </Typography>
                                </Box>
                                <Box display="flex">
                                    <Icon sx={{ mr: 1 }}>schedule</Icon>
                                    <Typography
                                        sx={theme => {
                                            theme.typography.pxToRem(14);
                                        }}
                                    >
                                        {formattedTime}
                                    </Typography>
                                </Box>
                            </CardActions>
                        </StyledCard>
                    );
                })}
            </Container>
        );
    };

    return (
        <Form
            key={isDrawerOpen}
            onSubmit={handleSubmit}
            initialValues={{
                message: selectedAlert.message,
                isSharedOnWorkOrder: selectedAlert.isSharedOnWorkOrder,
                isSharedOnLease: selectedAlert.isSharedOnLease
            }}
            enableReinitialize
            render={form => (
                <SideDrawer
                    title={showAdd ? 'Add Alert' : showEdit ? 'Edit Alert' : 'Alerts'}
                    drawerContentTypeID={DRAWER_CONTENT_TYPES.ALERTS}
                    ButtonProps={{ submitText: showAdd ? 'Save' : 'Update' }}
                    loading={form.isSubmitting}
                    onSubmit={form.handleSubmit}
                    onCancel={() => {
                        setShowAdd(false);
                        setShowEdit(false);
                        setSelectedAlert({});
                    }}
                    hideFooter={showAdd || showEdit ? false : true}
                >
                    {showAdd || showEdit ? (
                        <>
                            <TextField id="message" name="message" label="Alert" maxLength={100} multiline />
                            {allowWorkOrderShare(objectTypeID, contactTypeID) && (
                                <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>Show on Work Order</Typography>
                                    <SwitchField id="isSharedOnWorkOrder" name="isSharedOnWorkOrder" />
                                </Box>
                            )}
                            {allowLeaseShare(objectTypeID) && (
                                <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>Show on Lease</Typography>
                                    <SwitchField id="isSharedOnLease" name="isSharedOnLease" />
                                </Box>
                            )}
                            {allowLeasePaymentShare(objectTypeID) && (
                                <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>Show on Tenant Payment</Typography>
                                    <SwitchField id="isSharedOnLeasePayment" name="isSharedOnLeasePayment" />
                                </Box>
                            )}
                            {allowBillShare(objectTypeID, contactTypeID) && (
                                <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>Show on Bill</Typography>
                                    <SwitchField id="isSharedOnBill" name="isSharedOnBill" />
                                </Box>
                            )}
                        </>
                    ) : isEmpty(alerts) ? (
                        <EmptyMessage toggleAdd={toggleAdd} />
                    ) : (
                        <AlertsList />
                    )}
                    {renderDeleteAlert()}
                </SideDrawer>
            )}
        />
    );
}

const AlertsWrapper = withForm(AlertsButton);

const StyledCard = styled(Card)(({ theme }) => ({
    background: theme.palette.common.lightblue,
    border: '1px solid rgba(143,143,143,0.3)',
    borderRadius: '12px',
    marginBottom: theme.spacing(4),
    boxShadow: 'none'
}));

export { Alerts, AlertsWrapper as AlertsButton };
